// frontend/src/components/RichTextEditor.js
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { useEffect, useRef } from "react";

// Custom font size extension using inline styles
const FontSize = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: (element) => element.style.fontSize || null,
        renderHTML: (attributes) => {
          if (!attributes.fontSize) return {};
          return { style: `font-size: ${attributes.fontSize}` };
        },
      },
    };
  },
});

const FONT_SIZES = [
  { label: "Small", value: "0.875rem" },
  { label: "Normal", value: "1rem" },
  { label: "Large", value: "1.125rem" },
  { label: "XL", value: "1.25rem" },
];

const ToolbarButton = ({ active, onClick, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`px-2 py-0.5 text-xs leadline-border-thin ${
      active ? "bg-[#1E1E1E] text-[#F5EDE0]" : "bg-white text-[#3A3530]"
    }`}
  >
    {children}
  </button>
);

function RichTextEditor({ value, onChange, minHeight = "4rem" }) {
  const suppressUpdate = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        horizontalRule: false,
        strike: false,
        link: false,
        underline: false,
      }),
      Underline,
      TextAlign.configure({ types: ["paragraph"] }),
      FontSize,
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      if (suppressUpdate.current) return;
      const html = editor.getHTML();
      const isEmpty = html === "<p></p>" || html === "";
      onChange(isEmpty ? "" : html);
    },
  });

  // Sync external value changes (e.g., loading draft from Supabase)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const normalized = value || "";
    if (current !== normalized && normalized !== (current === "<p></p>" ? "" : current)) {
      suppressUpdate.current = true;
      editor.commands.setContent(normalized, { emitUpdate: false });
      suppressUpdate.current = false;
    }
  }, [value, editor]);

  if (!editor) return null;

  const currentFontSize = editor.getAttributes("textStyle").fontSize || "1rem";

  return (
    <div className="leadline-border-thin overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 px-2 py-1 bg-[#F9F5EE] border-b border-[#1E1E1E] border-opacity-20">
        <ToolbarButton
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline"
        >
          <u>U</u>
        </ToolbarButton>

        <span className="w-px h-4 bg-[#3A3530] opacity-20 mx-1" />

        <ToolbarButton
          active={editor.isActive({ textAlign: "left" })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          title="Align left"
        >
          &#9776;
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: "center" })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          title="Align center"
        >
          &#9868;
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive({ textAlign: "right" })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          title="Align right"
        >
          &#9782;
        </ToolbarButton>

        <span className="w-px h-4 bg-[#3A3530] opacity-20 mx-1" />

        <select
          value={currentFontSize}
          onChange={(e) => {
            const size = e.target.value;
            if (size === "1rem") {
              editor.chain().focus().unsetMark("textStyle").run();
            } else {
              editor.chain().focus().setMark("textStyle", { fontSize: size }).run();
            }
          }}
          className="text-xs leadline-border-thin px-1 py-0.5 bg-white"
        >
          {FONT_SIZES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none px-2 py-1 bg-white text-sm text-[#3A3530] focus-within:outline-none"
        style={{ minHeight }}
      />
    </div>
  );
}

export default RichTextEditor;
